import React from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { FiSettings, FiDollarSign, FiLock, FiBell, FiGlobe, FiLayout, FiArrowRight, FiSave, FiEyeOff } from 'react-icons/fi';
import { usePermissions } from '../../../contexts/PermissionsContext';
import GeneralSettings from './tabs/GeneralSettings';
import FinancialSettings from './tabs/FinancialSettings';
import SecuritySettings from './tabs/SecuritySettings';
import NotificationSettings from './tabs/NotificationSettings';
import SeoSettings from './tabs/SeoSettings';
import PersonalizationSettings from './tabs/PersonalizationSettings';

const Settings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const canManageSettings = hasPermission('manage_settings');
  
  // استخدام hash في URL للاحتفاظ بالتبويب النشط عند التحديث
  React.useEffect(() => {
    // إذا كنا في صفحة إعدادات محددة وليس هناك hash، نعين hash للتبويب الحالي
    if (location.pathname !== '/admin/dashboard/settings' && !location.hash) {
      const currentTab = location.pathname.split('/').pop();
      const newUrl = `${location.pathname}#${currentTab}`;
      // نستخدم window.history بدلاً من navigate لتجنب إعادة التحميل
      window.history.replaceState(null, '', newUrl);
    }
    // في حالة وجود hash ولكن غير متطابق مع المسار الحالي، ننتقل للتبويب المحدد في الهاش
    else if (location.hash && location.pathname === '/admin/dashboard/settings') {
      const tabFromHash = location.hash.substring(1); // إزالة علامة # من البداية
      const targetPage = settingsPages.find(page => page.id === tabFromHash);
      if (targetPage) {
        navigate(targetPage.path);
      }
    }
  }, [location, navigate]);

  const settingsPages = [
    { 
      id: 'general',
      title: 'عام',
      description: 'تغيير عنوان الموقع، والشعار، واللغة، و RTL، والحسابات الاجتماعية، وأنماط التصميم، والتحميل المسبق.',
      icon: FiSettings,
      path: '/admin/dashboard/settings/general',
      component: GeneralSettings,
      colors: {
        light: 'from-cyan-50 to-blue-100',
        dark: 'dark:from-cyan-900/40 dark:to-blue-800/40',
        glow: 'cyan-400',
        border: 'cyan-200 dark:cyan-600',
        icon: 'text-cyan-500 dark:text-cyan-300'
      }
    },
    { 
      id: 'financial',
      title: 'الماليات',
      description: 'تحديد العمولات والضرائب والدفع والعملة وبوابات الدفع والدفع الإلكتروني',
      icon: FiDollarSign,
      path: '/admin/dashboard/settings/financial',
      component: FinancialSettings,
      colors: {
        light: 'from-emerald-50 to-green-100',
        dark: 'dark:from-emerald-900/40 dark:to-green-800/40',
        glow: 'emerald-400',
        border: 'emerald-200 dark:emerald-600',
        icon: 'text-emerald-500 dark:text-emerald-300'
      }
    },
    { 
      id: 'security',
      title: 'الأمان',
      description: 'إعدادات الأمان وكلمات المرور والتحقق بخطوتين وسجلات الدخول',
      icon: FiLock,
      path: '/admin/dashboard/settings/security',
      component: SecuritySettings,
      colors: {
        light: 'from-purple-50 to-violet-100',
        dark: 'dark:from-purple-900/40 dark:to-violet-800/40',
        glow: 'purple-400',
        border: 'purple-200 dark:purple-600',
        icon: 'text-purple-500 dark:text-purple-300'
      }
    },
    { 
      id: 'notifications',
      title: 'الإشعارات',
      description: 'تعيين قوالب الإشعارات وتخصيص الرسائل والتنبيهات',
      icon: FiBell,
      path: '/admin/dashboard/settings/notifications',
      component: NotificationSettings,
      colors: {
        light: 'from-amber-50 to-orange-100',
        dark: 'dark:from-amber-900/40 dark:to-orange-800/40',
        glow: 'amber-400',
        border: 'amber-200 dark:amber-600',
        icon: 'text-amber-500 dark:text-amber-300'
      }
    },
    { 
      id: 'seo',
      title: 'SEO',
      description: 'حدد عنوان SEO ووصف التعريف والوصول إلى محرك البحث الزحف لكل صفحة',
      icon: FiGlobe,
      path: '/admin/dashboard/settings/seo',
      component: SeoSettings,
      colors: {
        light: 'from-rose-50 to-red-100',
        dark: 'dark:from-rose-900/40 dark:to-red-800/40',
        glow: 'rose-400',
        border: 'rose-200 dark:rose-600',
        icon: 'text-rose-500 dark:text-rose-300'
      }
    },
    { 
      id: 'personalization',
      title: 'الطابع الشخصي',
      description: 'تغيير خلفيات الصفحة والأقسام الرئيسية والصور والنصوص',
      icon: FiLayout,
      path: '/admin/dashboard/settings/personalization',
      component: PersonalizationSettings,
      colors: {
        light: 'from-slate-50 to-gray-100',
        dark: 'dark:from-slate-800/40 dark:to-gray-700/40',
        glow: 'slate-400',
        border: 'slate-200 dark:slate-600',
        icon: 'text-slate-500 dark:text-slate-300'
      }
    }
  ];

  // الصفحة الرئيسية للإعدادات
  if (location.pathname === '/admin/dashboard/settings') {
    return (
      <div className="p-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">إعدادات النظام</h2>
            </div>
          </div>
          <div className="p-6 relative">
            {!canManageSettings && (
              <div className="absolute inset-0 z-10 bg-gray-100/80 dark:bg-gray-900/80 backdrop-blur-[2px] rounded-b-lg flex flex-col items-center justify-center">
                <div className="bg-white dark:bg-gray-800 rounded-full p-3 mb-4 shadow-md">
                  <FiEyeOff className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-lg font-medium text-gray-600 dark:text-gray-300 mb-2 text-center">أنت في وضع العرض فقط</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center w-2/3">
                  تحتاج إلى صلاحية تعديل الإعدادات للقيام بأي تغييرات على هذه الصفحة
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {settingsPages.map((page) => (
                <div
                  key={page.id}
                  onClick={canManageSettings ? () => navigate(page.path) : undefined}
                  className={`rounded-lg p-6 bg-gradient-to-br ${page.colors.light} ${page.colors.dark} shadow-lg dark:shadow-gray-900/50 ${canManageSettings ? 'cursor-pointer hover:shadow-xl dark:hover:shadow-gray-900/70' : 'cursor-not-allowed'} transition-all duration-200`}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`p-3 rounded-full bg-white dark:bg-gray-800 ${page.colors.icon}`}>
                      <page.icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">{page.title}</h3>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{page.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // صفحات الإعدادات الفرعية
  return (
    <div className="p-6 relative">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
        {!canManageSettings && (
          <div className="absolute inset-0 bg-gray-100/80 dark:bg-gray-900/80 backdrop-blur-[2px] z-50 flex flex-col items-center justify-center rounded-lg">
            <div className="bg-white dark:bg-gray-800 rounded-full p-3 mb-4 shadow-md">
              <FiEyeOff className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-lg font-medium text-gray-600 dark:text-gray-300 mb-2 text-center">أنت في وضع العرض فقط</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center w-2/3">
              تحتاج إلى صلاحية تعديل الإعدادات للقيام بأي تغييرات على هذه الصفحة
            </p>
          </div>
        )}
        <Routes>
          <Route path="/" element={<Navigate to="/admin/dashboard/settings" replace />} />
          {settingsPages.map((page) => (
            <Route
              key={page.id}
              path={page.id}
              element={<page.component />}
            />
          ))}
        </Routes>
      </div>
    </div>
  );
};

export default Settings;
